/**
 * Pure helpers behind the company page's hub tabs and Overview cards:
 * conference phase, and the per-card summaries. Kept apart from the
 * components (same split as views/Telemetry/model.ts) so they test without
 * rendering.
 */
import type { Company, Conference, User } from '@viq-admin/types';
import type { CrmConnectionRow } from '@viq-admin/screens';
import { roleLabel } from '@viq-admin/ui';
import type { DeviceInstall } from '../../../api/telemetry';
import { compareVersions } from '../../../views/Telemetry/model';
import { COMPANY_CRM_FLAGS } from '../companyFlagHelp';

// ── Conferences ───────────────────────────────────────────────────────

export type ConferencePhase = 'live' | 'upcoming' | 'past';

export const PHASE_LABEL: Record<ConferencePhase, string> = {
  live: 'Live',
  upcoming: 'Upcoming',
  past: 'Past',
};

export const PHASE_COLOR: Record<ConferencePhase, string> = {
  live: 'green',
  upcoming: 'blue',
  past: 'default',
};

/** Live first, then upcoming, then past — the order the tab lists them in. */
export const PHASE_RANK: Record<ConferencePhase, number> = { live: 0, upcoming: 1, past: 2 };

/**
 * Today as `YYYY-MM-DD` in the viewer's local time.
 *
 * Conference start/end are Postgres DATEs (`YYYY-MM-DD`, a calendar day, not
 * an instant). Parsing one with `new Date()` reads it as UTC midnight, which
 * puts the day boundary in the wrong place anywhere off UTC (see
 * `formatDate` in packages/ui). Comparing the strings against the local
 * calendar day avoids `Date` entirely; ISO dates sort correctly as plain
 * strings. Same approach as the Batch rail's `todayISO`
 * (packages/screens/src/batch/eventGrouping.ts).
 */
export function todayISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Live while today falls within start..end (inclusive), by calendar day. */
export function conferencePhase(
  c: Pick<Conference, 'startDate' | 'endDate'>,
  today: string = todayISO(),
): ConferencePhase {
  if (!c.startDate || !c.endDate) return 'past';
  if (today < c.startDate) return 'upcoming';
  if (today > c.endDate) return 'past';
  return 'live';
}

/**
 * Live (latest start first), then upcoming (soonest first), then past
 * (most recently ended first): the events an operator is most likely asking
 * about come first.
 */
export function sortByPhase(conferences: Conference[], today: string = todayISO()): Conference[] {
  return [...conferences].sort((a, b) => {
    const pa = conferencePhase(a, today);
    const pb = conferencePhase(b, today);
    if (pa !== pb) return PHASE_RANK[pa] - PHASE_RANK[pb];
    if (pa === 'upcoming') return a.startDate.localeCompare(b.startDate);
    if (pa === 'past') return b.endDate.localeCompare(a.endDate);
    return b.startDate.localeCompare(a.startDate);
  });
}

export interface ConferenceSummary {
  live: number;
  upcoming: number;
  past: number;
  /** The live event (latest start if several), else null. */
  current: Conference | null;
  /** The soonest upcoming event, else null. */
  next: Conference | null;
}

export function summarizeConferences(
  conferences: Conference[],
  today: string = todayISO(),
): ConferenceSummary {
  const sorted = sortByPhase(conferences, today);
  const counts = { live: 0, upcoming: 0, past: 0 };
  for (const c of sorted) counts[conferencePhase(c, today)] += 1;
  return {
    ...counts,
    current: sorted.find((c) => conferencePhase(c, today) === 'live') ?? null,
    next: sorted.find((c) => conferencePhase(c, today) === 'upcoming') ?? null,
  };
}

// ── Users ─────────────────────────────────────────────────────────────

export const ROLE_COLOR: Record<string, string> = {
  field_rep: 'green',
  company_admin: 'blue',
  stakeholder: 'gold',
};

export interface UserSummary {
  active: number;
  inactive: number;
  /** Active users per role, most common first. */
  byRole: Array<{ role: string; label: string; count: number }>;
  /** The user who signed in most recently, or null if nobody has. */
  lastLogin: User | null;
}

export function summarizeUsers(users: User[]): UserSummary {
  let active = 0;
  let inactive = 0;
  const roles = new Map<string, number>();
  let lastLogin: User | null = null;
  for (const u of users) {
    if (u.deactivatedAt) {
      inactive += 1;
    } else {
      active += 1;
      roles.set(u.role, (roles.get(u.role) ?? 0) + 1);
    }
    if (u.lastLoginAt && (!lastLogin || u.lastLoginAt > (lastLogin.lastLoginAt ?? ''))) {
      lastLogin = u;
    }
  }
  const byRole = [...roles.entries()]
    .map(([role, count]) => ({ role, label: roleLabel(role), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return { active, inactive, byRole, lastLogin };
}

// ── Devices ───────────────────────────────────────────────────────────

export interface DeviceSummary {
  installs: number;
  stranded: number;
  outdated: number;
  /** Highest dotted-numeric version among THIS company's installs. */
  newestVersion: string | null;
}

export function summarizeInstalls(installs: DeviceInstall[]): DeviceSummary {
  let newestVersion: string | null = null;
  for (const i of installs) {
    if (i.appVersion && (newestVersion === null || compareVersions(i.appVersion, newestVersion) > 0)) {
      newestVersion = i.appVersion;
    }
  }
  return {
    installs: installs.length,
    stranded: installs.filter((i) => i.stranded).length,
    outdated: installs.filter((i) => i.outdated).length,
    newestVersion,
  };
}

/** Stranded installs first (leads stuck on a phone), then most recently seen. */
export function sortInstalls(installs: DeviceInstall[]): DeviceInstall[] {
  return [...installs].sort(
    (a, b) => Number(b.stranded) - Number(a.stranded) || b.lastSeenAt.localeCompare(a.lastSeenAt),
  );
}

// ── CRM ───────────────────────────────────────────────────────────────

type CrmFlag = (typeof COMPANY_CRM_FLAGS)[number]['name'];

/** The company flag that entitles each CRM source. */
export const CRM_SOURCE_FLAG: Record<CrmConnectionRow['crmSource'], CrmFlag> = {
  hubspot: 'crmHubspotEnabled',
  salesforce: 'crmSalesforceEnabled',
  zoho: 'crmZohoEnabled',
};

/**
 * Whether the company's flag for this connection's provider is on. An
 * unknown provider has no flag to check, so it reads as on.
 */
export function crmFlagOn(company: Company, crmSource: string): boolean {
  const flag = CRM_SOURCE_FLAG[crmSource as CrmConnectionRow['crmSource']];
  return flag ? Boolean(company[flag]) : true;
}

/** Labels of the CRM providers this company's flags enable, in console order. */
export function enabledCrmLabels(company: Company): string[] {
  return COMPANY_CRM_FLAGS.filter(({ name }) => company[name]).map(({ label }) => label);
}

/** Absent means staff-entered credentials (see ConnectionsTable's `managedByLabel`). */
export function managedByLabel(managedBy: CrmConnectionRow['managedBy']): string {
  return managedBy === 'oauth' ? 'OAuth (customer connect)' : 'Credentials (staff)';
}
